import { memo, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSelector } from "react-redux";
import classes from "./EditOrderDatesModal.module.scss";
import ColorButtons from "../ColorButtons";
import SingleDateField from "../../Calendar/SingleDateField";
import { getUserInfo } from "../../../reducks/users/selectors";
import { updateOrderDatesToFireStore } from "../../../functions/orders";
import {
  CHANGE_FIELD_LABEL,
  CHANGE_LOG_FIELD,
  isSameDateValue,
  ORDER_DATE_FIELDS,
  toDateOrNull,
  validateOrderDates,
} from "../../../functions/order/orderDateUpdate";
import { buildAutoRecordedEditNote } from "../../../functions/order/orderDateMessages";
import { getFiscalYear } from "../../../functions/deducation/fiscalYear";
import {
  formatOrderDate,
  formatOrderDateTime,
} from "../../../functions/utils/formatting";

const style = {
  box: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    // 日付欄(カレンダー付き)＋クリアボタン＋期の注記が横に並ぶので、
    // 確認ダイアログ(400px前後)より広めにとる
    width: { xs: "92%", sm: 480 },
    bgcolor: "background.paper",
    border: "none",
    borderRadius: "10px",
    boxShadow: 24,
    padding: "20px 0",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

// 変更履歴の値を表示用に整形する(statusは文字列、日付はTimestamp)
const renderChangeValue = (field, value) =>
  field === "status" ? value || "—" : formatOrderDate(value);

// 注文1明細の日付だけを後から編集するダイアログ。
//
// ステータス変更を伴わずに注文日・発注日・取消日を是正するための画面。
// 注文日は購入上限の集計(placed_at >= 期首)の基準なので、変更すると帰属する期が
// 変わることを必ず明示する。
//
// 検収日(autoRecorded)は編集の対象外。給与控除は検収日で月を締めるため、
// 遡って登録できると控除漏れになる。現在値とその理由だけを表示する。
export const EditOrderDatesModal = memo((props) => {
  const { order, siblings = [], onClose } = props;
  const userInfo = useSelector(getUserInfo);
  const now = useMemo(() => new Date(), []);

  const [values, setValues] = useState(() =>
    ORDER_DATE_FIELDS.reduce(
      (acc, { field }) => ({ ...acc, [field]: toDateOrNull(order?.[field]) }),
      {}
    )
  );
  const [applyToCart, setApplyToCart] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const { errors, warnings } = validateOrderDates({
    values,
    status: order?.status,
    now,
  });

  // 変更されたフィールドだけを更新対象にする。
  // (同じカートの他明細へ適用するとき、触っていない日付まで上書きしないため)
  const changedInput = useMemo(
    () =>
      ORDER_DATE_FIELDS.reduce((acc, { field }) => {
        if (isSameDateValue(order?.[field], values[field])) return acc;
        return { ...acc, [field]: values[field] };
      }, {}),
    [order, values]
  );
  const hasChanges = Object.keys(changedInput).length > 0;

  const currentFiscalYear = getFiscalYear(toDateOrNull(order?.placed_at));
  const nextFiscalYear = getFiscalYear(values.placed_at);
  const fiscalYearChanged =
    currentFiscalYear != null &&
    nextFiscalYear != null &&
    currentFiscalYear.startYear !== nextFiscalYear.startYear;

  const changeLogs = Array.isArray(order?.[CHANGE_LOG_FIELD])
    ? [...order[CHANGE_LOG_FIELD]].reverse()
    : [];

  const handleChange = (field, date) => {
    setValues((prev) => ({ ...prev, [field]: date }));
    setSaveError("");
  };

  const handleSave = async () => {
    if (errors.length > 0 || isSaving) return;
    // 変更が無いときは書き込まずに閉じる(押しても何も起きない状態を作らない)
    if (!hasChanges) {
      onClose();
      return;
    }
    setIsSaving(true);
    setSaveError("");
    try {
      const targets = applyToCart ? [order, ...siblings] : [order];
      await updateOrderDatesToFireStore(targets, changedInput, userInfo);
      onClose();
    } catch (error) {
      setSaveError(
        "日付の更新に失敗しました。通信環境を確認して再度お試しください。"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      // カレンダーのポップアップはPortalでbody直下(=このModalの外)に描画されるため、
      // フォーカストラップが有効だと日付を選んだ瞬間に焦点を奪い返されてしまう
      disableEnforceFocus
      aria-labelledby="edit-order-dates-title"
    >
      <Box sx={style.box}>
        <div className={classes.root}>
          <div className={classes.title} id="edit-order-dates-title">
            <EventNoteIcon className={classes.iconImage} />
            {`日付の編集：注文 ${order?.order_number ?? ""}`}
          </div>
          <div className={classes.subTitle}>
            {`${order?.name ?? ""}　現在の状態：${order?.status ?? "—"}`}
          </div>

          <div className={classes.fields}>
            {ORDER_DATE_FIELDS.map(({ field, label, autoRecorded }) => (
              <div key={field} className={classes.fieldRow}>
                {autoRecorded ? (
                  // 自動記録の日付(検収日)は表示だけ。
                  // 遡って登録できると給与控除の月次の締めから漏れるため、
                  // 変更もクリアも用意しない(ドメイン側でも無視している)。
                  <>
                    <div className={classes.lockedMain}>
                      <LockOutlinedIcon className={classes.lockIcon} />
                      <span className={classes.lockedLabel}>{label}</span>
                      <span className={classes.lockedValue}>
                        {formatOrderDate(order?.[field])}
                      </span>
                    </div>
                    <div className={classes.lockedNote}>
                      {buildAutoRecordedEditNote({
                        label,
                        status: order?.status,
                        value: order?.[field],
                      })}
                    </div>
                  </>
                ) : (
                  <div className={classes.fieldMain}>
                    <SingleDateField
                      label={label}
                      value={values[field]}
                      onChange={(date) => handleChange(field, date)}
                      width={170}
                    />
                    <button
                      type="button"
                      className={classes.clearButton}
                      onClick={() => handleChange(field, null)}
                      disabled={!values[field] || field === "placed_at"}
                      title={
                        field === "placed_at"
                          ? "注文日は必須のためクリアできません"
                          : `${label}をクリアします`
                      }
                    >
                      クリア
                    </button>
                  </div>
                )}
                {field === "placed_at" && (
                  <div className={classes.fiscalNote}>
                    {nextFiscalYear
                      ? fiscalYearChanged
                        ? `⚠ 購入上限のカウント対象期が ${currentFiscalYear.label} → ${nextFiscalYear.label} に変わります（${nextFiscalYear.label} = ${nextFiscalYear.rangeLabel}）`
                        : `${nextFiscalYear.label}（${nextFiscalYear.rangeLabel}）の購入としてカウントされます`
                      : "注文日が未設定です"}
                  </div>
                )}
              </div>
            ))}
          </div>

          {errors.length > 0 && (
            <div className={classes.errors}>
              {errors.map((message) => (
                <div key={message}>{message}</div>
              ))}
            </div>
          )}
          {warnings.length > 0 && (
            <div className={classes.warnings}>
              {warnings.map((message) => (
                <div key={message}>{`⚠ ${message}`}</div>
              ))}
            </div>
          )}

          {siblings.length > 0 && (
            <label className={classes.applyToCart}>
              <Checkbox
                size="small"
                checked={applyToCart}
                onChange={(event) => setApplyToCart(event.target.checked)}
              />
              <span>{`同じカートの他 ${siblings.length} 件にも同じ変更を適用する`}</span>
            </label>
          )}

          <button
            type="button"
            className={classes.historyToggle}
            onClick={() => setShowHistory((prev) => !prev)}
          >
            {`${showHistory ? "▲" : "▼"} 変更履歴 (${changeLogs.length})`}
          </button>
          {showHistory && (
            <div className={classes.history}>
              {changeLogs.length === 0 ? (
                <div className={classes.historyEmpty}>
                  変更履歴はありません（この機能の導入前の変更は記録されていません）
                </div>
              ) : (
                changeLogs.map((log, index) => (
                  <div
                    key={`${log?.by ?? ""}-${index}`}
                    className={classes.historyItem}
                  >
                    <div className={classes.historyHead}>
                      {`${formatOrderDateTime(log?.at)}　${log?.by ?? ""}`}
                    </div>
                    {(log?.changes ?? []).map((change, changeIndex) => (
                      <div
                        key={`${change?.field}-${changeIndex}`}
                        className={classes.historyChange}
                      >
                        {`${
                          CHANGE_FIELD_LABEL[change?.field] ?? change?.field
                        }　${renderChangeValue(
                          change?.field,
                          change?.from
                        )} → ${renderChangeValue(change?.field, change?.to)}`}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}

          {saveError && <div className={classes.errors}>{saveError}</div>}

          <div className={classes.buttons}>
            <ColorButtons
              label={isSaving ? "保存中..." : "保存"}
              color="placed"
              onClick={handleSave}
              // エラーがあるときは handleSave が何もしないので、押せないことを見た目でも示す
              disabled={errors.length > 0 || isSaving}
              width={120}
              height={32}
              bottom={0}
            />
            <ColorButtons
              label={"キャンセル"}
              color="grey"
              onClick={onClose}
              width={120}
              height={32}
              bottom={0}
            />
          </div>
          {!hasChanges && (
            <div className={classes.noChange}>変更はありません</div>
          )}
        </div>
      </Box>
    </Modal>
  );
});

export default EditOrderDatesModal;
